import { Card, Col, Container, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { Link, NavLink } from 'react-router-dom';
import { useScrollToTop } from '../Hooks/useScrollToTop';
import { getConfigSelector, homeDataSelector } from '../Redux/selectors';
import { isBookingOpen as bookingIsOpen } from '../utils';
import { IMAGE_URL } from '../const';

function Home() {
  useScrollToTop();

  return (
    <>
      <HomeHelmet />

      <HomeHeroContainer>
        <Container fluid className="home-hero p-0 d-flex flex-column position-relative">
          <img
            src={`${IMAGE_URL}/c950-hero.webp`}
            alt="Holiday destination"
            className="home-hero-img"
            fetchPriority="high"
            decoding="async"
            loading="eager"
          />
          <Container className="flex-grow-1 d-flex align-items-center justify-content-center flex-column">
            <HomeHeroHeadings />
            <HomeHeroButtons />
          </Container>

          <div className="waves light" />
        </Container>

        <HomeHeroBanner />

        <Container fluid className="p-0">
          <div className="waves light flip" />
        </Container>
      </HomeHeroContainer>

      <HomeColumns />
    </>
  );
}

export default Home;

const HomeHeroContainer = (props) => <Container className="home-hero-container" {...props} />;

const HomeHelmet = () => {
  const config = useSelector(getConfigSelector);

  return (
    <Helmet>
      <title key="title">{`Club £${config.promotion} Holidays | The Sun Holidays`}</title>
      <meta
        name="description"
        content={`Britain's biggest and best bargain breaks are back! Join The Sun's Club ${config.promotion} to collect points to choose your holidays.`}
      />
      <body className="bg-white" />
    </Helmet>
  );
};

const HomeHeroHeadings = () => {
  const { hero } = useSelector(homeDataSelector);

  if (!hero) return null;
  return (
    <Row>
      <Col className="text-center text-white text-shadow-dark">
        <h1>{hero?.heading}</h1>
        {hero?.subhead.length > 0 && (
          <p className="lead w-75 mx-auto font-size-sm-4" style={{ fontSize: '2rem', fontWeight: 400 }}>
            {hero.subhead}
          </p>
        )}
      </Col>
    </Row>
  );
};

const HomeHeroButtons = () => {
  const config = useSelector(getConfigSelector);
  const { hero } = useSelector(homeDataSelector);
  const isBookingOpen = bookingIsOpen(config);

  if (!hero) return null;

  return (
    <Row className="mt-3">
      <Col xs={12} lg={isBookingOpen ? 6 : 12} className="hero-button">
        {hero.buttonUrl.startsWith('/') ? (
          <NavLink to={hero.buttonUrl} className="btn btn-primary btn-lg text-truncate">
            {hero.buttonText}
          </NavLink>
        ) : (
          <a href={hero.buttonUrl} className="btn btn-primary btn-lg" target="_blank" rel="noopener noreferrer">
            {hero.buttonText}
          </a>
        )}
      </Col>
      {isBookingOpen && (
        <Col xs={12} lg={6} className="hero-button">
          <a
            href="https://www.ukholidaysinthesun.co.uk/"
            className="btn btn-primary btn-lg"
            target="_blank"
            rel="noopener noreferrer"
          >
            Book Now
          </a>
        </Col>
      )}
    </Row>
  );
};

const HomeHeroBanner = () => {
  const { banner } = useSelector(homeDataSelector);

  if (!banner?.active) return null;

  return (
    <Container fluid className="bg-light">
      <Container className="px-5 py-3">
        <p
          className="text-center m-0"
          style={{ fontSize: '2rem' }}
          dangerouslySetInnerHTML={{ __html: banner?.heading }}
        />
      </Container>
    </Container>
  );
};

const HomeColumns = () => {
  const { columns } = useSelector(homeDataSelector);

  if (!columns || columns.length === 0) return null;

  return (
    <Container>
      <Row xs={1} md={2} xl={3} className="pb-3">
        {columns.map((col, i) => (
          <Col className="p-4 d-flex" key={i}>
            <Card className="box-shadow flex-grow-1 homepage-card">
              {/^http/.test(col.buttonUrl) ? (
                <a href={col.buttonUrl} target="_blank" rel="noreferrer noopener">
                  <Card.Img variant="top" src={col.image} alt={col.title} />
                </a>
              ) : (
                <Link to={col.buttonUrl}>
                  <Card.Img variant="top" src={col.image} alt={col.title} />
                </Link>
              )}
              <Card.Title className="p-3 m-0">{col.title}</Card.Title>
              <Card.Text className="px-3 flex-grow-1" dangerouslySetInnerHTML={{ __html: col.content }} />
              <Card.Footer>
                {/^http/.test(col.buttonUrl) ? (
                  <a
                    href={col.buttonUrl}
                    className="btn btn-primary btn-block btn-lg"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {col.buttonText}
                  </a>
                ) : (
                  <Link to={col.buttonUrl} className="btn btn-primary btn-block btn-lg">
                    {col.buttonText}
                  </Link>
                )}
              </Card.Footer>
            </Card>
          </Col>
        ))}
      </Row>
    </Container>
  );
};
